import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PORTFOLIO } from '../constants/portfolio';
import '../styles/PageLoader.scss';

const EASE = [0.16, 1, 0.3, 1];
const EASE_SNAP = [0.76, 0, 0.24, 1];

const PageLoader = ({ onComplete }) => {
  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState('count');
  const rafRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (phase === 'wipe') {
      document.body.style.overflow = '';
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== 'count') return undefined;

    const duration = 1400;
    const start = performance.now();
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2.2) / 2);

    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setCount(Math.floor(ease(p) * 100));
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setCount(100);
        setTimeout(() => setPhase('reveal'), 280);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'reveal') return undefined;
    const t = setTimeout(() => setPhase('wipe'), 900);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {phase !== 'done' && (
        <motion.div
          key="loader"
          className="page-loader"
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
        >
          <AnimatePresence>
            {phase === 'count' && (
              <motion.div
                key="hud"
                className="page-loader__hud"
                exit={{ opacity: 0, y: -6, transition: { duration: 0.35, ease: EASE } }}
              >
                <motion.div
                  className="page-loader__brand"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.5, ease: EASE }}
                >
                  <span className="page-loader__dot" />
                  <span>{PORTFOLIO.shortName}</span>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                >
                  <div className="page-loader__count-row">
                    <span>Loading</span>
                    <span className={`page-loader__count ${count >= 80 ? 'page-loader__count--hot' : ''}`}>
                      {String(count).padStart(3, '0')}
                    </span>
                  </div>
                  <div className="page-loader__bar">
                    <motion.div
                      className="page-loader__bar-fill"
                      style={{ scaleX: count / 100 }}
                    />
                    <motion.div
                      className="page-loader__bar-knob"
                      style={{ left: `${count}%`, opacity: count > 2 ? 1 : 0 }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {phase === 'reveal' && (
              <motion.div
                key="reveal"
                className="page-loader__reveal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
              >
                <div className="page-loader__letters">
                  {'WELCOME'.split('').map((char, i) => (
                    <motion.span
                      key={i}
                      initial={{ y: '110%', opacity: 0 }}
                      animate={{ y: '0%', opacity: 1 }}
                      transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </div>
                <motion.div
                  className="page-loader__line"
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: EASE, delay: 0.35 }}
                />
                <motion.span
                  className="page-loader__subtitle"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: EASE, delay: 0.5 }}
                >
                  Conversational AI Developer
                </motion.span>
              </motion.div>
            )}
          </AnimatePresence>

          {phase === 'wipe' && (
            <motion.div
              className="page-loader__wipe"
              initial={{ y: '0%' }}
              animate={{ y: '-100%' }}
              transition={{ duration: 0.75, ease: EASE_SNAP, delay: 0.05 }}
              onAnimationComplete={() => setPhase('done')}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageLoader;
